import { ClusterSimThresholdType } from '@common/state/modules/mediaDetails/models';
import { DAG_REFERENCE_IDS } from './constants';
import {
  speakerSeparationEngineTask,
  speakerSeparationIngestRoutes,
  speakerSeparationOutputWriterTask,
} from './speakerSeparationDag';

/**
 * The TDO's media as a URL an engine can download — the platform's
 * media-streamer endpoint, NOT a signed asset URI (VE-30662).
 *
 * Two reasons it is this and not `primaryAsset.signedUri`:
 *
 *   - A signed URI is a time-limited bearer capability to the unredacted
 *     media, and an engine payload is a durable, QUERYABLE record:
 *     `Task.payload` and `Task.payloadString` are both readable by anyone who
 *     can read the task. VE-30513 removed that exposure from the ingestion
 *     payload; this keeps the post-ingestion runs off it too.
 *   - This path needs no signature at all. Engines recognise the
 *     media-streamer URL shape and inject their own auth bearer token when
 *     they download, which is what head detection has always relied on.
 *
 * The `.trim()` is inherited from the head-detection call site this replaced.
 * It lands on the finished string, so it only strips leading/trailing
 * whitespace — a newline INSIDE a configured `apiRoot` would survive it. Kept
 * as-is rather than tightened because every environment config sets `apiRoot`
 * and this whole construction goes away with VE-30658, which drops `url` from
 * these payloads entirely.
 */
export const mediaStreamerDownloadUrl = (apiRoot: string, tdoId: string) =>
  `${apiRoot}/media-streamer/download/tdo/${tdoId}`.trim();

const DEFAULT_AUDIO_CHUNK_DURATION = 180;
const DEFAULT_VIDEO_CHUNK_DURATION = 60;
const DEFAULT_VIDEO_CHUNK_OVERLAP = 3;
const DEFAULT_AUDIO_CHUNK_OVERLAP = 3;

// Image transcode constants
// const MAX_IMAGE_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
// const IMAGE_OUTPUT_QUALITY = 100;

export interface Task {
  engineId: string;
  payload?: any;
  executionPreferences?: {
    priority?: number;
    parentCompleteBeforeStarting?: boolean;
    maxEngines?: number;
  };
  ioFolders?: Array<{
    referenceId: string;
    mode: string;
    type: string;
  }>;
}

export interface IngestionTask extends Task {
  payload: {
    chunkSize?: number;
    clusterSimThreshold?: ClusterSimThresholdType;
    faceDetectionThreshold?: number;
    fileType?: string;
    tdoId?: string;
    url?: string;
    user?: string; // user's email
    zeropadOffsetAudio?: boolean;
    legacyClustering?: boolean;
    rotate?: number;
    chunkDuration?: number;
    chunkOverlap?: number;
    chunkType?: string;
  };
  executionPreferences?: {
    priority: number;
  };
  ioFolders?: Array<{
    referenceId: string;
    mode: string;
    type: string;
  }>;
}

interface ChunkerTask extends Task {
  payload: {
    comment?: string;
    app?: string;
    url?: string;
    chunkType?: string;
    chunkDurationSecs: number;
    chunkOverlapSecs: number;
  };
  executionPreferences: {
    priority: number;
    parentCompleteBeforeStarting?: boolean;
  };
  ioFolders: Array<{
    referenceId: string;
    mode: string;
    type: string;
  }>;
}

interface DetectionTask extends Task {
  payload: {
    confidenceThreshold: number;
    videoType: string;
    stepSizeDetection: number;
    app: string;
    detectHead: boolean;
    detectLaptop: boolean;
    detectLicense: boolean;
    detectVehicle: boolean;
    detectNotepad: boolean;
    detectCard: boolean;
    detectPerson: boolean;
    mergeVehiclePlate: string;
    mergePersonHead: boolean;
    detectorType: string;
    legacyClustering?: boolean;
    clusterSimThreshold?: ClusterSimThresholdType;
  };
  executionPreferences: {
    maxEngines: number;
    parentCompleteBeforeStarting: boolean;
    priority: number;
  };
  ioFolders: Array<{
    referenceId: string;
    mode: string;
    type: string;
  }>;
}

interface TranscriptionTask extends Task {
  payload: {
    app: string;
    diarise: string;
  };
  executionPreferences: {
    maxEngines: number;
    parentCompleteBeforeStarting: boolean;
    priority: number;
  };
  ioFolders: Array<{
    referenceId: string;
    mode: string;
    type: string;
  }>;
}
interface VoiceMaskTasks extends Task {
  executionPreferences: {
    maxEngines: number;
    parentCompleteBeforeStarting: boolean;
    priority: number;
  };
  ioFolders: Array<{
    referenceId: string;
    mode: string;
    type: string;
  }>;
}

interface OutputWriterTask extends Task {
  executionPreferences: {
    parentCompleteBeforeStarting: boolean;
    priority: number;
  };
  ioFolders: Array<{
    referenceId: string;
    mode: string;
    type: string;
  }>;
}

export enum DetectionType {
  HEAD = 'head',
  TRANSCRIPTION = 'transcription',
}

interface Props {
  url?: string;
  isImage?: boolean;
  glcIngestionEngineId?: string;
  voiceMaskEngineId?: string;
  outputWriterEngineId: string;
  fastChunkerEngineId?: string;
  transcriptionEngineId?: string;
  transcriptionCategoryId?: string;
  transcriptionEngineOptions?: Record<string, unknown>;
  speakerSeparationEngineId?: string;
  runTranscription?: boolean;
  runVoiceMask?: boolean;
  runDetection?: boolean;
  ingestionPayload?: IngestionTask['payload'];
  voiceMask?: boolean;
  detectionOptions?: {
    engineId: string;
    confidenceThreshold: number;
    videoType: string;
    detectionRate: number;
    detectHead?: boolean;
    detectNotepad?: boolean;
    detectCard?: boolean;
    detectPerson?: boolean;
    legacyClustering?: boolean;
    clusterSimThreshold?: ClusterSimThresholdType;
  };
}

const transcriptionChunkerPayload = (
  chunkLengthSeconds: number,
  url?: string
) => ({
  comment: 'Fast Audio Chunk',
  app: 'redact',
  chunkType: 'audio',
  chunkDurationSecs: chunkLengthSeconds,
  chunkOverlapSecs: DEFAULT_AUDIO_CHUNK_OVERLAP,
  ...(url && { url }),
});

/**
 * Speaker separation and voice mask both want the whole timeline but only the
 * audio track. glc_fast_chunker has no "emit one chunk" switch — the only lever
 * is a chunk duration longer than the media, which leaves its segment loop with
 * nothing to close and makes it fall through to the single trailing segment.
 * The overlap is only applied where a chunk is closed mid-file, so its value is
 * never reached here; it just has to clear the engine's `0 < overlap < duration`
 * validation.
 *
 * TODO: ask for a proper single-chunk option on glc_fast_chunker (a flag, or
 * treating chunkDurationSecs: 0 as "whole file") and switch to it. Expressing
 * "one chunk" as an absurd duration is a trick that depends on the shape of
 * the engine's segment loop, and nothing on the engine side stops that shape
 * changing underneath us.
 */
const SINGLE_CHUNK_DURATION = 1_000_000; // ~11.5 days, far beyond any media we ingest
const SINGLE_CHUNK_OVERLAP = 1;

const singleAudioChunkerPayload = () => ({
  comment: 'Fast Audio Chunk (whole file)',
  app: 'redact',
  chunkType: 'audio',
  chunkDurationSecs: SINGLE_CHUNK_DURATION,
  chunkOverlapSecs: SINGLE_CHUNK_OVERLAP,
});

const detectionChunkerPayload = (chunkLengthSeconds: number, url?: string) => ({
  comment: 'Fast Video Chunk',
  app: 'redact',
  chunkDurationSecs: chunkLengthSeconds,
  chunkOverlapSecs: DEFAULT_VIDEO_CHUNK_OVERLAP,
  ...(url && { url }),
});

interface Route {
  parentIoFolderReferenceId: string;
  childIoFolderReferenceId: string;
  options: unknown;
}

/** Build DAG task and routes for jobs involving any combination of ingestion, transcription and object detection */
export function generateTasksAndRoutes({
  url,
  glcIngestionEngineId,
  outputWriterEngineId,
  fastChunkerEngineId,
  transcriptionEngineId,
  transcriptionCategoryId,
  transcriptionEngineOptions,
  speakerSeparationEngineId,
  runTranscription,
  runVoiceMask,
  runDetection,
  ingestionPayload,
  detectionOptions,
  voiceMaskEngineId,
  voiceMask,
  isImage,
}: Props) {
  const routes: Route[] = [];
  const tasks: Task[] = [];

  const runIngestion = !!ingestionPayload;

  // disable transcription and voice mask for images
  // eslint-disable-next-line no-param-reassign -- destructured props narrowed for image TDOs
  runTranscription = runTranscription && !isImage;
  // eslint-disable-next-line no-param-reassign -- destructured props narrowed for image TDOs
  runVoiceMask = runVoiceMask && !isImage;

  const runSpeakerSeparation = !!(
    runTranscription &&
    speakerSeparationEngineId &&
    runIngestion
  );

  // Conditions for needing audio chunker
  // if also doing head detection will need chunker
  // or if using previously ingested
  // or is using voiceMask need to prevent more than 1 chunk going to voice mask engine
  // or speaker separation is enabled (transcription-only ingestor chunk path is not valid)
  const needAudioChunker =
    (runTranscription || runVoiceMask) &&
    (runDetection || !runIngestion || voiceMask || runSpeakerSeparation);

  // Speaker separation reads one chunk covering the whole audio, which the
  // ingestor gives it for free — except when detection runs in the same job,
  // where the ingestor has to emit video and the engine would pull the entire
  // video file to read its audio track. A fast chunker set to a single audio
  // chunk strips the video once.
  //
  // Voice mask has the same inefficiency and rides along when the chunker is
  // present for separation, but does not justify it on its own: adding a task
  // to the detection + voice-mask graph would change a shipped flow that has
  // nothing to do with this feature.
  const needSingleAudioChunker = !!(
    fastChunkerEngineId &&
    runDetection &&
    runSpeakerSeparation
  );
  const fullAudioFolder = needSingleAudioChunker
    ? 'singleAudioOutputFolder'
    : 'ingestOutputFolder';

  if (ingestionPayload && glcIngestionEngineId) {
    // check if using ingestion engine to produce chunks instead of fast chunker
    if (runDetection && !(runTranscription || runVoiceMask)) {
      ingestionPayload['chunkDuration'] = DEFAULT_VIDEO_CHUNK_DURATION;
      ingestionPayload['chunkOverlap'] = DEFAULT_VIDEO_CHUNK_OVERLAP;
    }

    // IMAGE-TODO: force to image output chunk to video for now since object detection is not supported for images
    if (isImage) {
      ingestionPayload['chunkType'] = 'video';
    }

    if (!runDetection && (runTranscription || runVoiceMask)) {
      if (!needAudioChunker) {
        // if not using audio chunker can use glc-ingestor built-in chunker
        ingestionPayload['chunkDuration'] = DEFAULT_AUDIO_CHUNK_DURATION;
        ingestionPayload['chunkOverlap'] = DEFAULT_AUDIO_CHUNK_OVERLAP;
      }
      ingestionPayload['chunkType'] = 'audio';
    }

    tasks.push({
      engineId: glcIngestionEngineId,
      payload: ingestionPayload,
      executionPreferences: {
        priority: -20,
      },
      ...((runDetection ||
        runTranscription ||
        runVoiceMask ||
        runSpeakerSeparation) &&
        ({
          // check if need output
          ioFolders: [
            {
              referenceId: 'ingestOutputFolder',
              mode: 'chunk',
              type: 'output',
            },
          ],
        } as IngestionTask)),
    });
  }

  // ---------------------------------------
  // add object detection
  if (runDetection && detectionOptions) {
    // if also doing transcription will need split into two chunkers (or if ingesting previously ingested)
    const useVideoChunker = runTranscription || runVoiceMask || !runIngestion; // eventually will able to skip chunker in some scenarios

    const hasParent = !!runIngestion;

    if (isImage && !hasParent) {
      // for images that have already been ingested we use the GLC ingestor in playback false mode to load image and convert to video instead of the glc chunker
      if (!glcIngestionEngineId) {
        throw new Error(
          'glcIngestionEngineId is required when processing an image without a parent ingestion task.'
        );
      }
      tasks.push({
        engineId: glcIngestionEngineId,
        payload: {
          playback: false,
          chunkType: 'video',
        },
        executionPreferences: {
          priority: -20,
        },
        ioFolders: [
          {
            referenceId: 'ingestOutputFolder',
            mode: 'chunk',
            type: 'output',
          },
        ],
      } as IngestionTask);

      routes.push({
        // load image --> Head Detection
        parentIoFolderReferenceId: 'ingestOutputFolder',
        childIoFolderReferenceId: 'hdInputFolder',
        options: {},
      });
    } else if (useVideoChunker) {
      tasks.push({
        engineId: fastChunkerEngineId,
        payload: detectionChunkerPayload(
          DEFAULT_VIDEO_CHUNK_DURATION,
          hasParent ? undefined : url // if parent engine uses input chunk instead of url
        ),
        executionPreferences: {
          priority: -5,
          ...(runIngestion && { parentCompleteBeforeStarting: true }),
        },
        ioFolders: hasParent
          ? [
              {
                referenceId: 'chunkVideoInputFolder',
                mode: 'chunk',
                type: 'input',
              },
              {
                referenceId: 'chunkVideoOutputFolder',
                mode: 'chunk',
                type: 'output',
              },
            ]
          : [
              {
                referenceId: 'chunkVideoOutputFolder',
                mode: 'chunk',
                type: 'output',
              },
            ],
      } as ChunkerTask);

      // add routes
      if (runIngestion) {
        routes.push({
          // ingest to chunkVideo
          parentIoFolderReferenceId: 'ingestOutputFolder',
          childIoFolderReferenceId: 'chunkVideoInputFolder',
          options: {},
        });
      }

      routes.push({
        // chunkVideo --> Head Detection
        parentIoFolderReferenceId: 'chunkVideoOutputFolder',
        childIoFolderReferenceId: 'hdInputFolder',
        options: {},
      });
    } else {
      // otherwise will go directly from ingestor chunks to head detection
      routes.push({
        // ingestor --> Head Detection
        parentIoFolderReferenceId: 'ingestOutputFolder',
        childIoFolderReferenceId: 'hdInputFolder',
        options: {},
      });
    }

    const {
      engineId: headEngineId,
      confidenceThreshold,
      videoType,
      detectionRate,
      detectHead,
      detectNotepad,
      detectCard,
      detectPerson,
      legacyClustering,
      clusterSimThreshold,
    } = detectionOptions;

    tasks.push({
      engineId: headEngineId,
      payload: {
        confidenceThreshold,
        videoType: isImage ? 'CCTV' : videoType, // hack to get better results for images - IMAGE-TODO pass in image mode option to engine once supported
        ...(isImage && { reshapePerson: false, labelPersonAsHead: false }), // disable these for now
        stepSizeDetection: detectionRate,
        app: 'redact',
        detectHead: !!detectHead,
        detectLaptop: true,
        detectLicense: true,
        detectVehicle: true,
        detectNotepad: !!detectNotepad,
        detectCard: !!detectCard,
        detectPerson: !!detectPerson,
        mergeVehiclePlate: 'plate',
        detectorType: 'full',
        mergePersonHead: false,
        ...(legacyClustering && { legacyClustering }),
        ...(clusterSimThreshold && { clusterSimThreshold }),
      },
      executionPreferences: {
        maxEngines: 20,
        parentCompleteBeforeStarting: true,
        priority: -5,
      },
      ioFolders: [
        {
          referenceId: 'hdInputFolder',
          mode: 'chunk',
          type: 'input',
        },
        {
          referenceId: 'hdOutputFolder',
          mode: 'chunk',
          type: 'output',
        },
      ],
    } as DetectionTask);

    // add outputwriter
    tasks.push({
      engineId: outputWriterEngineId,
      executionPreferences: {
        parentCompleteBeforeStarting: true,
        priority: -15,
      },
      ioFolders: [
        {
          referenceId: DAG_REFERENCE_IDS['owFromHead'],
          mode: 'chunk',
          type: 'input',
        },
      ],
    } as OutputWriterTask);

    routes.push({
      // HeadDetection --> output writer
      parentIoFolderReferenceId: 'hdOutputFolder',
      childIoFolderReferenceId: DAG_REFERENCE_IDS['owFromHead'],
      options: {},
    });
  }

  // ---------------------------------------
  // single full-length audio chunk, shared by speaker separation and voice mask
  if (needSingleAudioChunker) {
    tasks.push({
      engineId: fastChunkerEngineId,
      payload: singleAudioChunkerPayload(),
      executionPreferences: {
        parentCompleteBeforeStarting: true,
        priority: -5,
      },
      ioFolders: [
        {
          referenceId: 'singleAudioInputFolder',
          mode: 'chunk',
          type: 'input',
        },
        {
          referenceId: 'singleAudioOutputFolder',
          mode: 'chunk',
          type: 'output',
        },
      ],
    } as ChunkerTask);

    routes.push({
      // ingest --> singleAudio
      parentIoFolderReferenceId: 'ingestOutputFolder',
      childIoFolderReferenceId: 'singleAudioInputFolder',
      options: {},
    });
  }

  // ---------------------------------------
  // voice mask
  if (runVoiceMask && voiceMask && runIngestion) {
    tasks.push({
      engineId: voiceMaskEngineId,
      executionPreferences: {
        parentCompleteBeforeStarting: true,
        priority: -15,
      },
      ioFolders: [
        {
          referenceId: 'voiceMaskInputFolder',
          mode: 'chunk',
          type: 'input',
        },
      ],
    } as VoiceMaskTasks);

    // add routes
    routes.push({
      // ingest (or the single-audio chunker) --> voiceMask
      parentIoFolderReferenceId: fullAudioFolder,
      childIoFolderReferenceId: 'voiceMaskInputFolder',
      options: {},
    });
  }

  // ---------------------------------------
  // transcription
  if (runTranscription) {
    if (needAudioChunker) {
      tasks.push({
        engineId: fastChunkerEngineId,
        payload: transcriptionChunkerPayload(
          DEFAULT_AUDIO_CHUNK_DURATION,
          runIngestion ? undefined : url // if parent engine uses input chunk instead of url
        ),
        executionPreferences: {
          ...(runIngestion && { parentCompleteBeforeStarting: true }),
          priority: -5,
        },
        ioFolders: !runIngestion
          ? [
              {
                referenceId: 'chunkAudioOutputFolder',
                mode: 'chunk',
                type: 'output',
              },
            ]
          : [
              {
                referenceId: 'chunkAudioInputFolder',
                mode: 'chunk',
                type: 'input',
              },
              {
                referenceId: 'chunkAudioOutputFolder',
                mode: 'chunk',
                type: 'output',
              },
            ],
      } as ChunkerTask);

      // add routes
      if (runIngestion) {
        routes.push({
          // ingest --> chunkAudio
          parentIoFolderReferenceId: 'ingestOutputFolder',
          childIoFolderReferenceId: 'chunkAudioInputFolder',
          options: {},
        });
      }

      routes.push({
        // chunkAudio --> transcription
        parentIoFolderReferenceId: 'chunkAudioOutputFolder',
        childIoFolderReferenceId: 'transcriptionInputFolder',
        options: {},
      });
    } else {
      // otherwise will go directly from ingestor chunks to to transcription engine
      routes.push({
        // ingestor --> transcription
        parentIoFolderReferenceId: 'ingestOutputFolder',
        childIoFolderReferenceId: 'transcriptionInputFolder',
        options: {},
      });
    }

    tasks.push({
      engineId: transcriptionEngineId,
      payload: Object.assign({}, transcriptionEngineOptions, {
        diarise: 'false',
        // for the whisper transcription engine - harmless to include for other transcription engines
        wordTimeStamps: true,
        app: 'redact',
      }),
      executionPreferences: {
        maxEngines: 10,
        parentCompleteBeforeStarting: true,
        priority: -5,
      },
      ioFolders: [
        {
          referenceId: 'transcriptionInputFolder',
          mode: 'chunk',
          type: 'input',
        },
        {
          referenceId: 'transcriptionOutputFolder',
          mode: 'chunk',
          type: 'output',
        },
      ],
    } as TranscriptionTask);

    tasks.push({
      engineId: outputWriterEngineId,
      executionPreferences: {
        parentCompleteBeforeStarting: true,
        priority: -15,
      },
      ioFolders: [
        {
          referenceId: DAG_REFERENCE_IDS['owFromTranscription'],
          mode: 'chunk',
          type: 'input',
        },
      ],
    } as OutputWriterTask);

    routes.push({
      // Transcription --> output writer
      parentIoFolderReferenceId: 'transcriptionOutputFolder',
      childIoFolderReferenceId: DAG_REFERENCE_IDS['owFromTranscription'],
      options: {},
    });
  }

  // ---------------------------------------
  // Speaker separation, in parallel with the chunked transcription branch. It
  // takes the full-length audio chunk — from the ingestor, or from the
  // single-audio chunker when detection forces the ingestor to emit video.
  //
  // The two supported engines reach the same result differently, which is why
  // the payload is per-engine (see speakerSeparationDag): Speechmatics, which
  // is what the config points at, diarizes the audio itself; the Veritone
  // engine instead waits for the transcription task in this same job
  // (useTranscript) and seeds its boundaries from that transcript's
  // punctuation timing. The graph shape serves both.
  if (runSpeakerSeparation && speakerSeparationEngineId) {
    tasks.push(
      speakerSeparationEngineTask(speakerSeparationEngineId, {
        // Both ids: the engine id is the transcription task in this very job,
        // the category covers whichever engine ends up producing it. The two
        // paths used to send one each (review B7).
        transcriptEngineId: transcriptionEngineId,
        transcriptCategoryId: transcriptionCategoryId,
      }),
      speakerSeparationOutputWriterTask(outputWriterEngineId)
    );
    routes.push(...speakerSeparationIngestRoutes(fullAudioFolder));
  }

  return [tasks, routes];
}

// IMAGE-TODO: Don't use this function - it's here for reference only of older implemention

/**
 * Generate DAG tasks and routes for image ingestion using alternate engines
 * SI2 (rawchunk) -> Image Transcode -> Thumbnail Generator
 * Note: Not used by default
 */

/* interface ImageIngestionProps {
  url: string;
  si2EngineId: string;
  imageTranscodeEngineId: string;
  thumbnailGeneratorEngineId: string;
  outputWriterEngineId: string;
  runDetection: boolean;
  detectionOptions?: {
    engineId: string;
    confidenceThreshold: number;
    videoType: string;
    detectionRate: number;
    detectHead?: boolean;
  };
}
 */

/* export function generateImageIngestionTasksAndRoutes_DONT_USE_THIS({
  url,
  si2EngineId,
  imageTranscodeEngineId,
  thumbnailGeneratorEngineId,
  outputWriterEngineId,
  runDetection,
  detectionOptions,
}: ImageIngestionProps): [Array<Task>, Array<Route>] {
  const tasks: Array<Task> = [];
  const routes: Array<Route> = [];

  // SI2 Engine - rawchunk
  tasks.push({
    engineId: si2EngineId,
    payload: {
      url,
      // eslint-disable-next-line @cspell/spellchecker
      ffmpegTemplate: 'rawchunk',
    },
    executionPreferences: {
      priority: -5,
    },
    ioFolders: [
      {
        referenceId: 'si2OutputFolder',
        mode: 'chunk',
        type: 'output',
      },
    ],
  });

  // Image Transcode Engine
  tasks.push({
    engineId: imageTranscodeEngineId,
    payload: {
      setAsPrimary: true,
      operationType: 'convert',
      convertToSrip: true,
      maxFileSizeBytes: MAX_IMAGE_FILE_SIZE_BYTES,
      convertOutputFormat: 'jpeg',
      convertOutputQuality: IMAGE_OUTPUT_QUALITY,
    },
    executionPreferences: {
      priority: -10,
      parentCompleteBeforeStarting: true,
    },
    ioFolders: [
      {
        referenceId: 'transcodeInputFolder',
        mode: 'chunk',
        type: 'input',
      },
      {
        referenceId: 'transcodeOutputFolder',
        mode: 'chunk',
        type: 'output',
      },
    ],
  });

  // Head Detection Engine (optional - as child of Image Transcode)
  if (runDetection && detectionOptions) {
    const {
      engineId,
      confidenceThreshold,
      videoType,
      detectionRate,
      detectHead,
    } = detectionOptions;

    tasks.push({
      engineId,
      payload: {
        confidenceThreshold,
        videoType,
        stepSizeDetection: detectionRate,
        app: 'redact',
        detectHead: !!detectHead,
        detectLaptop: true,
        detectLicense: true,
        detectVehicle: true,
        legacyClustering: false,
      },
      executionPreferences: {
        priority: -12,
        parentCompleteBeforeStarting: true,
      },
      ioFolders: [
        {
          referenceId: 'headDetectionInputFolder',
          mode: 'chunk',
          type: 'input',
        },
        {
          referenceId: 'headDetectionOutputFolder',
          mode: 'chunk',
          type: 'output',
        },
      ],
    } as Task);

    // add outputwriter
    tasks.push({
      engineId: outputWriterEngineId,
      executionPreferences: {
        parentCompleteBeforeStarting: true,
        priority: -15,
      },
      ioFolders: [
        {
          referenceId: DAG_REFERENCE_IDS['owFromHead'],
          mode: 'chunk',
          type: 'input',
        },
      ],
    } as OutputWriterTask);

    routes.push({
      // HeadDetection --> output writer
      parentIoFolderReferenceId: 'headDetectionOutputFolder',
      childIoFolderReferenceId: DAG_REFERENCE_IDS['owFromHead'],
      options: {},
    });
  }

  // Thumbnail Generator Engine
  tasks.push({
    engineId: thumbnailGeneratorEngineId,
    executionPreferences: {
      priority: -15,
      parentCompleteBeforeStarting: true,
    },
    ioFolders: [
      {
        referenceId: 'thumbnailInputFolder',
        mode: 'chunk',
        type: 'input',
      },
    ],
  } as Task);

  // Routes
  routes.push({
    // SI2 -> Image Transcode
    parentIoFolderReferenceId: 'si2OutputFolder',
    childIoFolderReferenceId: 'transcodeInputFolder',
    options: {},
  });

  // Add route for head detection if enabled
  if (runDetection && detectionOptions) {
    routes.push({
      // Image Transcode -> Head Detection
      parentIoFolderReferenceId: 'transcodeOutputFolder',
      childIoFolderReferenceId: 'headDetectionInputFolder',
      options: {},
    });
  }

  routes.push({
    // Image Transcode -> Thumbnail Generator
    parentIoFolderReferenceId: 'transcodeOutputFolder',
    childIoFolderReferenceId: 'thumbnailInputFolder',
    options: {},
  });

  return [tasks, routes];
}
 */
